import React from 'react';

import OverflowTextModal from '@rover/components/src/OverflowTextModal/OverflowTextModal';
import { Display, Flex } from '@rover/kibble/core';
import type { Behavior } from '@rover/rsdk/src/apiClient/latest/model/behavior';
import type { BehaviorCustom } from '@rover/rsdk/src/apiClient/latest/model/behaviorCustom';
import type { Image } from '@rover/rsdk/src/apiClient/latest/model/image';
import type { Skill } from '@rover/rsdk/src/apiClient/latest/model/skill';
import type { SkillCustom } from '@rover/rsdk/src/apiClient/latest/model/skillCustom';
import type { TrainerEducation } from '@rover/rsdk/src/apiClient/latest/model/trainerEducation';
import { TrainerProfileDetailsMethodology } from '@rover/rsdk/src/apiClient/latest/model/trainerProfileDetailsMethodology';

import MethodologySection from '../Methodology/MethodologySection';
import QualificationsSection from '../Qualifications/QualificationsSection';
import TextBadgeSection from '../TextBadgeSection';
import YearsOfExperience from '../YearsOfExperience';

type OverviewSectionProps = {
  behaviors: Behavior[];
  behaviorsCustom: BehaviorCustom[];
  headline: string;
  methodology?: TrainerProfileDetailsMethodology;
  methodologyCustom?: string;
  methodologyDetails?: string;
  yearsOfTraining: number;
  educationList: TrainerEducation[];
  description: string;
  name: string;
  defaultImage: Image;
  skills: Skill[];
  skillsCustom: SkillCustom[];
  variant?: 'simplified' | 'full';
};

export const AboutSection: React.FC<OverviewSectionProps> = ({
  behaviors,
  behaviorsCustom,
  headline,
  methodology,
  methodologyCustom,
  methodologyDetails,
  yearsOfTraining,
  educationList,
  description,
  name,
  defaultImage,
  skills,
  skillsCustom,
  variant = 'full',
}) => {
  if (variant === 'simplified') {
    return (
      <Flex flexDirection="column" data-testid="about-section-simplified-variant">
        <Flex flexDirection="column" mb="2x">
          <Display webProps={{ as: 'h2', responsive: false }} size="400" mb={['4x', '6x']}>
            {headline}
          </Display>
          <OverflowTextModal text={description} numberOfLines={4} textColor="secondary" />
        </Flex>
        <Flex flexDirection={['column', 'row', 'column', 'row']} gap={['4x', '10x', '4x', '10x']}>
          <TextBadgeSection
            heading="Behavior experience"
            items={behaviors}
            headingSize="200"
            itemsCustom={behaviorsCustom}
            isTrainerProfileReordering
          />
          <TextBadgeSection
            heading="Training skill set"
            items={skills}
            headingSize="200"
            itemsCustom={skillsCustom}
            isTrainerProfileReordering
          />
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex
      gap="6x"
      flexDirection={['column', 'row', 'column', 'row']}
      data-testid="about-section-full-variant"
    >
      <Flex flexDirection="column" flexBasis={['0', '457px', '0', '457px']}>
        <Display webProps={{ as: 'h2', responsive: false }} size="400" mb="1x">
          {headline}
        </Display>
        <OverflowTextModal text={description} numberOfLines={6} />
        <Flex flexDirection="column" display={['none', 'flex', 'none', 'flex']} gap="4x" py="6x">
          <TextBadgeSection
            heading="Behavior experience"
            items={behaviors}
            headingSize="300"
            itemsCustom={behaviorsCustom}
          />
          <TextBadgeSection
            heading="Training skill set"
            items={skills}
            headingSize="300"
            itemsCustom={skillsCustom}
          />
        </Flex>
      </Flex>
      <Flex flexDirection="column" gap="3x" flexBasis={['0', '340px', '0', '340px']}>
        <MethodologySection
          methodology={methodology}
          methodologyCustom={methodologyCustom}
          methodologyDetails={methodologyDetails}
          name={name}
          defaultImage={defaultImage}
        />

        <YearsOfExperience yearsOfTraining={yearsOfTraining} />

        <QualificationsSection educationList={educationList} name={name} />
      </Flex>
      <Flex flexDirection="column" display={['flex', 'none', 'flex', 'none']} gap="4x">
        <TextBadgeSection
          heading="Behavior experience"
          headingSize="300"
          items={behaviors}
          itemsCustom={behaviorsCustom}
        />
        <TextBadgeSection
          heading="Training skill set"
          headingSize="300"
          items={skills}
          itemsCustom={skillsCustom}
        />
      </Flex>
    </Flex>
  );
};
